import React, { useCallback } from 'react';
import type { SigningField } from '../types/fields';
import { getCompactTextFieldSize, getCompactTextFieldSizeInPoints } from '../utils/coordinates';
import { fieldTextLayoutMode, formLineBaselineOffsetFromBottom, isSingleLineFormText } from '../utils/textLayout';
import { TextFormatToolbar } from './TextFormatToolbar';
import { buildTextFormatUpdates, type TextFormatPatch } from '../utils/textFormatActions';
import { resolveFieldTextStyle } from '../utils/fontMapping';

interface FieldPropertiesPanelProps {
  field: SigningField | null;
  fields: SigningField[];
  formatTargetIds?: string[];
  onUpdate: (id: string, updates: Partial<SigningField>) => void;
  onApplyTextFormat?: (patch: TextFormatPatch, targetIds: string[]) => void;
  onDelete: (id: string) => void;
  signerProfile: { name: string; company: string; title: string };
}

export const FieldPropertiesPanel: React.FC<FieldPropertiesPanelProps> = ({
  field,
  fields,
  formatTargetIds = [],
  onUpdate,
  onApplyTextFormat,
  onDelete,
  signerProfile,
}) => {
  // Hooks must be at top level, before any early returns
  const toolbarTargetIds = React.useMemo(
    () => (formatTargetIds.length > 0 ? formatTargetIds : (field ? [field.id] : [])),
    [formatTargetIds, field]
  );

  const handleFormatApply = useCallback(
    (patch: TextFormatPatch) => {
      if (!field) return;
      const targetIds = toolbarTargetIds;
      if (onApplyTextFormat) {
        onApplyTextFormat(patch, targetIds);
        return;
      }
      for (const id of targetIds) {
        const f = fields.find((x) => x.id === id);
        if (f) onUpdate(id, buildTextFormatUpdates(f, patch));
      }
    },
    [fields, onApplyTextFormat, onUpdate, toolbarTargetIds, field]
  );

  if (!field) {
    return (
      // BUG-17: w-full fills the inspector column (was w-72 fixed); border-l removed to avoid double-border
      <div className="w-full bg-[var(--bg-panel)] p-5 text-sm text-[var(--text-muted)]">
        <div className="font-medium text-[var(--text-primary)] mb-2">Properties</div>
        <p>Select a field on the PDF to edit its value, size, or delete it.</p>
        <div className="mt-6 text-xs leading-relaxed border-t pt-4">
          Tip: Double-click a field on the PDF, or press Enter / F2, to edit in place. Paste with Ctrl+V.
        </div>
      </div>
    );
  }

  const updateValue = (value: string) => {
    const updates: Partial<SigningField> = { value };

    // Auto-compact the box for signature-type fields so the field hugs the text
    // instead of staying in a big empty rectangle (user request).
    if (
      ['signature-type', 'date', 'name', 'initials'].includes(field.type) ||
      (field.type === 'text' && isSingleLineFormText(value))
    ) {
      const fs = field.fontSize ?? (field.type === 'signature-type' ? 16 : 13);
      const tight = getCompactTextFieldSizeInPoints(field.type, value, fs, field);
      updates.width = tight.width;
      updates.height = tight.height;
    }

    // Keep y anchored for 'text' value edits in inspector (same logic as inline commit).
    if (field.type === 'text' && updates.height != null) {
      const finalH = updates.height as number;
      const finalMode = fieldTextLayoutMode(field.type, { boxHeight: finalH, value });
      const oldTop = field.y + (field.height || 0);
      if (finalMode === 'form-line') {
        const fs = field.fontSize ?? 13;
        const sty = resolveFieldTextStyle({ ...field, value } as any, finalMode);
        const fam = sty.metricsFamily || 'helvetica';
        const offset = formLineBaselineOffsetFromBottom(fs, fam);
        (updates as any).y = Math.max(0, Math.round(oldTop - offset));
      } else {
        (updates as any).y = Math.max(0, Math.round(oldTop - finalH));
      }
    }

    onUpdate(field.id, updates);
  };

  const isTextField = ['text', 'name', 'date', 'signature-type', 'initials'].includes(field.type);
  const isSignatureImage = ['signature-draw', 'signature-upload', 'image'].includes(field.type);
  const isCheckbox = field.type === 'checkbox';
  const valueInputId = `field-value-${field.id}`;
  const valueLabel = field.type === 'text' ? 'Text (paste supported)' : 'Text Value';

  return (
    // BUG-17: w-full fills inspector column; border-l removed to avoid double-border with parent
    <div className="w-full bg-[var(--bg-panel)] flex flex-col properties-panel overflow-auto">
      <div className="p-4 border-b">
        <div className="uppercase tracking-widest text-[10px] font-semibold text-[var(--text-muted)] mb-1">FIELD PROPERTIES</div>
        {/* BUG-25: map known types to friendly labels; fallback replaces ALL hyphens */}
        <div className="font-semibold text-lg capitalize">
          {({
            'signature-type': 'Typed Signature',
            'signature-draw': 'Drawn Signature',
            'signature-upload': 'Uploaded Signature',
          } as Record<string, string>)[field.type] ?? field.type.replace(/-/g, ' ')}
        </div>
        <div className="text-xs text-[var(--text-muted)]">Page {field.pageIndex + 1}</div>
      </div>

      <div className="p-4 space-y-4 text-sm flex-1">
        {/* Value editing */}
        {isTextField && (
          <div>
            <label
              htmlFor={valueInputId}
              className="block text-xs font-medium mb-1 text-[var(--text-secondary)]"
            >
              {valueLabel}
            </label>
            {field.type === 'text' ? (
              // BUG-10: plain Enter inserts newline; commit on onChange/onBlur only
              // BUG-04: apply field.color so inspector text matches canvas
              <textarea
                id={valueInputId}
                aria-label={valueLabel}
                value={field.value || ''}
                onChange={(e) => updateValue(e.target.value)}
                rows={3}
                className="w-full border border-[var(--border-hairline)] rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-accent resize-y min-h-[4rem] bg-[var(--bg-panel)] text-[var(--text-primary)]"
                placeholder="Type or paste text (Ctrl+V)."
                style={field.color ? { color: field.color } : undefined}
              />
            ) : (
              // BUG-04: apply field.color so inspector text matches canvas
              <input
                id={valueInputId}
                type="text"
                aria-label={valueLabel}
                value={field.value || ''}
                onChange={(e) => updateValue(e.target.value)}
                onFocus={(e) => e.target.select()}
                className="w-full border border-[var(--border-hairline)] rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-accent bg-[var(--bg-panel)] text-[var(--text-primary)]"
                placeholder={field.type === 'date' ? 'MM/DD/YYYY' : 'Enter text…'}
                style={field.color ? { color: field.color } : undefined}
              />
            )}
            {field.type === 'name' && signerProfile.name && (
              <button
                onClick={() => updateValue(signerProfile.name)}
                className="mt-1.5 text-xs text-accent hover:underline"
              >
                Use my saved name: {signerProfile.name}
              </button>
            )}
            {field.type === 'date' && (
              <button
                onClick={() => {
                  try {
                    updateValue(
                      new Date().toLocaleDateString(undefined, {
                        year: 'numeric',
                        month: '2-digit',
                        day: '2-digit',
                      })
                    );
                  } catch {
                    updateValue(new Date().toLocaleDateString());
                  }
                }}
                className="mt-1.5 text-xs text-accent hover:underline"
              >
                Insert today’s date
              </button>
            )}
          </div>
        )}

        {isSignatureImage && (
          <div>
            <label className="block text-xs font-medium mb-1 text-[var(--text-secondary)]">Signature Image</label>
            {field.imageDataUrl ? (
              <div className="border border-[var(--border-hairline)] rounded p-2 bg-[var(--bg-stage)]">
                <img src={field.imageDataUrl} alt="signature" className="max-h-16" />
              </div>
            ) : (
              <div className="text-xs text-state-warning">No image data stored for this field.</div>
            )}
            <div className="text-[11px] mt-1 text-[var(--text-muted)]">To change, delete this field and place a new signature.</div>
            {field.savedSignatureId && (
              <div className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-state-success-bg text-state-success px-2.5 py-0.5 text-[11px] font-medium">
                ✓ From your local library
              </div>
            )}
          </div>
        )}

        {isCheckbox && (
          <div>
            <label className="block text-xs font-medium mb-1 text-[var(--text-secondary)]">Checkbox State</label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={!!field.isChecked}
                onChange={(e) => onUpdate(field.id, { isChecked: e.target.checked })}
                className="w-4 h-4"
              />
              <span>Checked by default</span>
            </label>
          </div>
        )}

        {isTextField && (
          <div>
            <label className="block text-xs font-medium mb-2 text-[var(--text-secondary)]">Text formatting</label>
            <TextFormatToolbar
              fields={fields}
              targetIds={toolbarTargetIds}
              onApply={handleFormatApply}
              variant="inspector"
            />
          </div>
        )}

        {/* Compact "Fit to text" for signature-type and other short text fields.
            This directly solves the "big empty box around a small signature" problem. */}
        {isTextField && (
          <div className="pt-1">
            <button
              onClick={() => {
                const fs = field.fontSize ?? (field.type === 'signature-type' ? 16 : 13);
                const tight = getCompactTextFieldSize(field.type, field.value || '', fs, 1);
                onUpdate(field.id, { width: tight.width, height: tight.height });
              }}
              className="text-[11px] px-2.5 py-1 rounded border border-[var(--border-hairline)] hover:bg-[var(--bg-stage)] text-[var(--text-secondary)]"
              title="Shrink the field box to tightly fit the current text + font size (great for signatures on lines)"
            >
              Fit box to text
            </button>
            <div className="text-[10px] text-[var(--text-muted)] mt-0.5">Makes the box compact around the signature</div>
          </div>
        )}

        {/* Dimensions */}
        {field.type === 'signature-type' ? (
          <div className="text-xs pt-1 text-[var(--text-muted)]">
            Box size is automatic — it tightly hugs the signature text.<br />
            Use the Font Size slider above to make the signature bigger or smaller.
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3 text-xs pt-2">
            <div>
              <div className="text-[var(--text-muted)]">Width</div>
              <div className="font-medium tabular-nums">{field.width.toFixed(1)} pt</div>
            </div>
            <div>
              <div className="text-[var(--text-muted)]">Height</div>
              <div className="font-medium tabular-nums">{field.height.toFixed(1)} pt</div>
            </div>
          </div>
        )}
      </div>

      <div className="p-4 border-t mt-auto">
        <button
          onClick={() => onDelete(field.id)}
          className="w-full rounded-lg border border-state-danger-bg bg-state-danger-bg py-2 text-sm font-semibold text-state-danger hover:bg-state-danger-bg"
        >
          Delete This Field
        </button>
        <div className="text-[11px] text-center text-[var(--text-muted)] mt-2">
          You can also drag fields or use the small × on each field
        </div>
      </div>
    </div>
  );
};
